/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { resolveTrueVaultScriptUrl } from './truevault-consent-config';

/**
 * RGBARRY-CUSTOMIZATION (DRAFT — W-23789028 consent rework): TrueVault Polaris CMP bootstrap.
 * TrueVault is the customer's consent management platform (it replaced OneTrust on live SFRA);
 * this loads that CMP on SF Next and bridges it to Google Consent Mode v2 so GTM/GA4 tags respect
 * the shopper's choice. See docs/TRUEVAULT-CONSENT-MODE-PLAN.md.
 *
 * ⚠️ DRAFT / scaffold — env-gated (empty `scriptUrl` renders nothing) and not enabled anywhere yet.
 *
 * Per TrueVault docs (developers.truevault.com):
 *  - The loader is `https://polaris.truevaultcdn.com/static/pc/<Privacy Center ID>/polaris.js`.
 *  - Per TrueVault's Developer Instructions the loader MUST be the FIRST functional script in <head>,
 *    WITHOUT async/defer, and ABOVE Google Tag Manager, so Polaris evaluates the shopper's opt-out
 *    (esp. GA opt-outs) BEFORE gtm.js / the Google Analytics script runs. We follow that literally:
 *    polaris.js is emitted as a plain synchronous <script src> ahead of the GTM bootstrap. This is a
 *    DELIBERATE, MARKED deviation from this repo's rule "never load third-party scripts synchronously"
 *    (CLAUDE.md #20) — TrueVault's consent gate is only correct when it runs render-blocking ahead of
 *    the tags it governs, so the compliance requirement wins here (RGBARRY-CUSTOMIZATION). Cost is a
 *    one-time ~75ms render-blocking fetch (≈10–20ms cached). (The URL is origin-pinned to the TrueVault
 *    CDN host in truevault-consent-config.ts, so an arbitrary-origin env value cannot load.)
 *  - Polaris does NOT call gtag() itself. Two integration modes:
 *      • RECOMMENDED (bridgeConsentMode=false): install TrueVault's official "Polaris CMP" GTM
 *        Custom Template in the container; it registers Polaris's addConsentListener and calls
 *        GTM's setDefaultConsentState/updateConsentState.
 *      • MANUAL (bridgeConsentMode=true): we subscribe to Polaris's `consentChanged` window
 *        CustomEvent and translate event.detail into gtag('consent','update', …) ourselves.
 *
 * US-ONLY, ENABLED-BY-DEFAULT (customer's live config — latest findings): Baggallini operates in the
 * United States ONLY — there are NO opt-IN (GDPR/UK/CH/QC) regions to serve. Their TrueVault property
 * is therefore opt-OUT / enabled-by-default: categories are PERMITTED by default and the shopper's
 * "Do Not Sell or Share My Personal Information" choice arrives as `notOptedOut` in the
 * `consentChanged` detail. Two consequences, both taken here:
 *   1. Consent Mode `default` is GRANTED, not denied. A deny-all floor exists only to withhold tags
 *      until consent is affirmatively given — an opt-IN (GDPR) requirement this customer does not
 *      have. Granting by default matches "enabled by default" and avoids suppressing the initial
 *      analytics/ads hit. This inline default is tiny and non-blocking and still runs first.
 *   2. The Polaris loader is SYNCHRONOUS and first-in-<head> (see the loader note above), per
 *      TrueVault's spec: it runs and applies any stored opt-out BEFORE gtm.js, so there is no window
 *      in which an opted-out returning shopper's Google tags could fire. The granted default below only
 *      covers the very first pre-Polaris paint of a brand-new visitor, which under US opt-out is the
 *      correct posture (enabled by default until the shopper chooses "Do Not Sell or Share").
 *
 * We do NOT rely on TrueVault's site-scanner auto-blocking to stop our tags. Its scanner rewrites
 * script tags it can see in the DOM; it cannot see the Salesforce server-side engagement adapters
 * (einstein / data360 / activeData) which fire as server HTTP calls with no page script tag — so the
 * solid path is to gate on the opt-out signal explicitly (Consent Mode here for Google/GTM tags; the
 * engagement consentPreferences layer — use-analytics.ts + truevault-consent-store.ts — for the
 * server adapters; see docs/TRUEVAULT-CONSENT-MODE-PLAN.md §7).
 *
 * ENABLEMENT: the single switch is a valid https `scriptUrl` (see truevault-consent-config.ts), set
 * per MRT environment. There is NO deployment-host allowlist (which website hosts may run the CMP) —
 * a client-runtime host guard cannot be mirrored by the server-evaluated flag that disables the
 * built-in banner, and that divergence would strand a host with no consent UI at all. Distinct from
 * that: the scriptUrl's own origin IS pinned to the TrueVault CDN host in truevault-consent-config.ts
 * (so a bad env value can't load an arbitrary script) — that pin is mirrored on both sides by the
 * shared predicate, so it does not cause the strand-with-no-UI divergence.
 */

export interface TrueVaultConsentConfig {
    /** Full polaris.js URL, incl. the Privacy Center ID in the path. Empty/non-https disables the CMP. */
    scriptUrl: string;
    /** true = bridge `consentChanged` → gtag here. false = TrueVault's GTM Custom Template does it. */
    bridgeConsentMode: boolean;
}

export function TrueVaultConsent({
    consent,
    nonce,
}: {
    consent?: TrueVaultConsentConfig;
    nonce?: string;
}): ReactElement | null {
    // Env-gate + validation in one place (shared with config.server.ts): returns the normalized https
    // href (origin-pinned to the TrueVault CDN host) or null. The parsed href also neutralizes any
    // `</script>`-style breakout, and it is emitted as a React `src` attribute (auto-escaped).
    const scriptUrl = resolveTrueVaultScriptUrl(consent?.scriptUrl);
    if (!scriptUrl) return null;

    const bridgeConsentMode = consent?.bridgeConsentMode ?? false;

    // Enabled-by-default state (US opt-out) — ALWAYS emitted (both modes), tiny and non-blocking, first
    // in <head>. GRANTED by default because the customer is US-only / enabled-by-default (see docblock);
    // the synchronous loader (emitted last, below) then fires `consentChanged` to apply any opt-out on
    // top of this. Idempotent with a matching granted default from the GTM bootstrap / TrueVault GTM
    // Custom Template.
    const defaultScript = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){window.dataLayer.push(arguments);}
        gtag('consent','default',{
            'analytics_storage':'granted',
            'ad_storage':'granted',
            'ad_user_data':'granted',
            'ad_personalization':'granted',
            'personalization_storage':'granted',
            'functionality_storage':'granted',
            'security_storage':'granted'
        });
    `;

    // NOTE: no init-event capture script is emitted. The engagement opt-out store
    // (truevault-consent-store.ts) reads the shopper's stored choice synchronously from the first-party
    // `polaris_consent_settings` cookie at first render (TrueVault Eng, 2026-08-20, plan §10 answer 2.2),
    // so a returning opted-out shopper is correct with no pre-hydration `consentChanged` capture hack.

    // MANUAL bridge (bridgeConsentMode=true). Registered AFTER the default and BEFORE the loader, so
    // Polaris firing `consentChanged` on init lands its update on top of the established granted default
    // (in the US opt-out model the meaningful update is DENYING ad signals when the shopper opts out).
    // Mapping follows developers.truevault.com: analytics←analyticsPermitted;
    // ad_storage/ad_user_data/ad_personalization←adsPermitted (advertising; the `consentChanged` detail
    // exposes no separate targeted-advertising flag, so advertising is the documented fallback for
    // ad_personalization); personalization_storage←personalizationPermitted;
    // functionality_storage/security_storage←essentialPermitted (essential/necessary category).
    //
    // OPT-OUT SIGNAL (enabled-by-default / CCPA "Do Not Sell or Share"): the advertising signals are
    // granted only when adsPermitted AND the shopper has not opted out (`notOptedOut !== false`). In an
    // opt-out region the "Do Not Sell/Share" toggle governs the sale/sharing-for-advertising signals,
    // so opting out must deny ad_storage/ad_user_data/ad_personalization even while adsPermitted stays
    // true. `notOptedOut` is absent in pure opt-in (GDPR) regions, where `!== false` is a no-op and
    // adsPermitted alone decides — so this exact bridge is correct for BOTH the opt-out and opt-in
    // models. Analytics is intentionally NOT gated on notOptedOut (US opt-out covers sale/sharing of
    // data for advertising, not first-party analytics).
    const bridgeScript = bridgeConsentMode
        ? `
        (function(){
            function gtag(){window.dataLayer=window.dataLayer||[];window.dataLayer.push(arguments);}
            // Polaris fires 'consentChanged' on init, on every update, and on banner dismissal.
            // event.detail: { analyticsPermitted, personalizationPermitted, adsPermitted,
            //                 essentialPermitted, notOptedOut, ... }
            window.addEventListener('consentChanged', function(event){
                var d = (event && event.detail) || {};
                var g = function(v){ return v ? 'granted' : 'denied'; };
                // Advertising = permitted AND not opted out (see OPT-OUT SIGNAL note above).
                var adsGranted = d.adsPermitted && d.notOptedOut !== false;
                gtag('consent','update',{
                    'analytics_storage': g(d.analyticsPermitted),
                    'ad_storage': g(adsGranted),
                    'ad_user_data': g(adsGranted),
                    'ad_personalization': g(adsGranted),
                    'personalization_storage': g(d.personalizationPermitted),
                    'functionality_storage': g(d.essentialPermitted),
                    'security_storage': g(d.essentialPermitted)
                });
            });
        })();
    `
        : '';

    return (
        <>
            {/* oxlint-disable-next-line react/no-danger */}
            <script nonce={nonce} suppressHydrationWarning dangerouslySetInnerHTML={{ __html: defaultScript }} />
            {bridgeScript ? (
                // oxlint-disable-next-line react/no-danger
                <script nonce={nonce} suppressHydrationWarning dangerouslySetInnerHTML={{ __html: bridgeScript }} />
            ) : null}
            {/* Loader: SYNCHRONOUS, first functional <script> in <head>, ahead of the GTM bootstrap —
                exactly as TrueVault's Developer Instructions require (no async/defer, above GTM) so
                Polaris evaluates opt-outs before gtm.js / GA run. This is a marked, deliberate deviation
                from CLAUDE.md #20 (no synchronous third-party scripts): the consent gate is only correct
                when it blocks ahead of the tags it governs (RGBARRY-CUSTOMIZATION). The tiny inline
                Consent Mode `default` above is the Google-recommended pre-CMP stub (not an analytics
                tag), so emitting it immediately before the loader keeps polaris.js the first tag-bearing
                script. scriptUrl is the origin-pinned, normalized href; React escapes the src attribute. */}
            <script src={scriptUrl} nonce={nonce} suppressHydrationWarning />
        </>
    );
}
